/**
 * Number, currency, percent, and date formatting.
 *
 * One module so every figure in the product is formatted the same way, and so
 * the rules are unit-testable without rendering anything (SPEC §13).
 *
 * `null` and `undefined` render as an em dash rather than "0" or "NaN": a
 * missing value and a zero mean different things in a report, and conflating
 * them is the kind of quiet wrongness this system must never produce.
 */

/** Rendered in place of a value that is absent. */
export const EMPTY = '—'

export type Nullish = null | undefined

const isAbsent = (value: number | Nullish): value is Nullish =>
  value === null || value === undefined || Number.isNaN(value)

const LOCALE = 'en-US'

export interface NumberOptions {
  /** Fixed decimal places. Omit to let the value decide (0–2). */
  readonly decimals?: number
  /** Thousands separators. Default true. */
  readonly grouping?: boolean
}

export function formatNumber(value: number | Nullish, options: NumberOptions = {}): string {
  if (isAbsent(value)) return EMPTY
  const { decimals, grouping = true } = options
  return new Intl.NumberFormat(LOCALE, {
    useGrouping: grouping,
    minimumFractionDigits: decimals ?? 0,
    maximumFractionDigits: decimals ?? 2,
  }).format(value)
}

export function formatInteger(value: number | Nullish): string {
  return formatNumber(value, { decimals: 0 })
}

export function formatCurrency(
  value: number | Nullish,
  currency = 'USD',
  options: { readonly decimals?: number } = {},
): string {
  if (isAbsent(value)) return EMPTY
  const decimals = options.decimals ?? 2
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)
}

/**
 * @param value a ratio, so 0.0725 renders as "7.25%".
 */
export function formatPercent(value: number | Nullish, decimals = 1): string {
  if (isAbsent(value)) return EMPTY
  return new Intl.NumberFormat(LOCALE, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)
}

/** Compact form for axis labels and KPI tiles: 12500 -> "12.5K". */
export function formatCompact(value: number | Nullish, decimals = 1): string {
  if (isAbsent(value)) return EMPTY
  return new Intl.NumberFormat(LOCALE, {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: decimals,
  }).format(value)
}

/**
 * Signed delta, for period-over-period columns. Always carries an explicit
 * sign so a decline is never mistaken for a small positive.
 */
export function formatDelta(value: number | Nullish, options: NumberOptions = {}): string {
  if (isAbsent(value)) return EMPTY
  const formatted = formatNumber(Math.abs(value), options)
  if (value === 0) return formatted
  return `${value > 0 ? '+' : '−'}${formatted}`
}

export type DateInput = Date | string | number | Nullish

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** 2026-07-25 — sortable, unambiguous, and what a report table should show. */
export function formatDate(value: DateInput): string {
  const date = toDate(value)
  if (date === null) return EMPTY
  return date.toISOString().slice(0, 10)
}

/** 2026-07-25 14:32 in the viewer's local zone, for freshness stamps. */
export function formatDateTime(value: DateInput): string {
  const date = toDate(value)
  if (date === null) return EMPTY
  return new Intl.DateTimeFormat(LOCALE, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
    .format(date)
    .replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$1-$2')
}

/** "4 minutes ago" — the freshness badge (SPEC §9.3). */
export function formatRelative(value: DateInput, now: Date = new Date()): string {
  const date = toDate(value)
  if (date === null) return EMPTY

  const seconds = Math.round((date.getTime() - now.getTime()) / 1000)
  const units: readonly (readonly [Intl.RelativeTimeFormatUnit, number])[] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['day', 86_400],
    ['hour', 3600],
    ['minute', 60],
    ['second', 1],
  ]
  const formatter = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' })

  for (const [unit, unitSeconds] of units) {
    if (Math.abs(seconds) >= unitSeconds) {
      return formatter.format(Math.round(seconds / unitSeconds), unit)
    }
  }
  return formatter.format(0, 'second')
}

// --- manifest-driven formatting ----------------------------------------------
//
// Everything below takes a ColumnSpec and dispatches on its `kind`, so one
// column is rendered identically wherever it appears — a table cell, a chart
// tooltip, a KPI tile, an axis label (SPEC §9.4). A caller that reaches past
// these for its own `toFixed` is how a currency ends up with two decimals in
// the table and none on the axis.

import type { Cell, ColumnSpec } from './types'

/** A column's value as text. The only approved way to render a report cell. */
export function formatCell(column: ColumnSpec, value: Cell): string {
  if (value === null) return EMPTY
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'

  switch (column.kind) {
    case 'integer':
      return formatInteger(asNumber(value))
    case 'number':
      return formatNumber(
        asNumber(value),
        column.decimals === undefined ? {} : { decimals: column.decimals },
      )
    case 'currency':
      return formatCurrency(asNumber(value), 'USD', { decimals: column.decimals ?? 2 })
    case 'percent':
      return formatPercent(asNumber(value), column.decimals ?? 1)
    case 'date':
      return formatDate(value)
    case 'datetime':
      return formatDateTime(value)
    case 'string':
      return String(value)
  }
}

/** The compact form for a dense axis. Same rules, fewer characters. */
export function formatAxisValue(column: ColumnSpec, value: Cell): string {
  if (value === null) return EMPTY
  if (column.kind === 'date' || column.kind === 'datetime') return formatCell(column, value)
  if (isNumericKind(column)) return formatCompact(asNumber(value))
  return String(value)
}

export function isNumericKind(column: ColumnSpec): boolean {
  return (
    column.kind === 'number' ||
    column.kind === 'integer' ||
    column.kind === 'currency' ||
    column.kind === 'percent'
  )
}

/**
 * A column's default alignment. Numbers right so their digits line up under
 * each other, which is the entire reason for the tabular figures (§9.2).
 */
export function columnAlign(column: ColumnSpec): 'left' | 'right' | 'center' {
  return column.align ?? (isNumericKind(column) ? 'right' : 'left')
}

/**
 * A cell as a number, or `null` if it is not one.
 *
 * Deliberately strict: a value the manifest calls a number and the source sent
 * as text is a manifest bug, and rendering `NaN` as `0` would hide it.
 */
export function asNumber(value: Cell): number | null {
  if (value === null || typeof value === 'boolean') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isNaN(parsed) ? null : parsed
}

/** A column's totals row figure, per `ColumnSpec.total` (§9.4). */
export function columnTotal(column: ColumnSpec, values: readonly Cell[]): number | null {
  if (column.total === 'none') return null
  const numbers = values.map(asNumber).filter((value): value is number => value !== null)
  if (numbers.length === 0) return null
  const sum = numbers.reduce((carry, value) => carry + value, 0)
  return column.total === 'avg' ? sum / numbers.length : sum
}
